import { useState, useRef, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { SystemAboutPanel } from '@/components/common/SystemAboutPanel';
import { panelLinksForRoles, openInBrowser } from '@/constants/panelLinks';
import { useNavigate } from 'react-router';

function initialsOf(name: string | undefined, upnFallback: string | undefined): string {
  const src = (name ?? '').trim();
  if (src) {
    const parts = src.split(/\s+/).filter(Boolean).slice(0, 2);
    const joined = parts.map((p) => p[0]?.toUpperCase() ?? '').join('');
    if (joined) return joined;
  }
  const upn = (upnFallback ?? '').split('@')[0] ?? '';
  if (upn.length >= 2) return upn.slice(0, 2).toUpperCase();
  if (upn.length === 1) return upn.toUpperCase();
  return '?';
}

export function UserMenu(): JSX.Element {
  const { user } = useAuth();
  const [open, setOpen] = useState<boolean>(false);
  const navigate = useNavigate();
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent): void => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    window.addEventListener('mousedown', handler);
    return () => window.removeEventListener('mousedown', handler);
  }, [open]);

  const initials = initialsOf(user?.displayName, user?.upn);
  const myRoles = (user?.roles ?? []).map((r) => String(r).toLowerCase());
  const panelLinks = panelLinksForRoles(myRoles);

  return (
    <div ref={wrapRef} className="shell__user-menu">
      <button
        type="button"
        className="shell__user-menu-btn" data-tour="topbar-user"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : 'false'}
        aria-label={user?.displayName ?? user?.upn ?? 'User menu'}
      >
        {initials}
      </button>
      {open && (
        <div className="shell__user-menu-pop">
          <div className="shell__user-menu-pop__id">
            <div className="shell__user-menu-pop__name">
              {user?.displayName ?? 'Unknown user'}
            </div>
            <div className="shell__user-menu-pop__upn">{user?.upn}</div>
            <div className="shell__user-menu-pop__role">
              Role: {user?.primaryRole ?? 'Officer'}
            </div>
          </div>
          <hr className="shell__user-menu-pop__divider" />
          {/* CHG-0100 (Connor 2026-10-23): your life-ring reports and requests, from the Hub change tracker.
              0.162.0 (Bill 2026-11-04): opens the Pilot & requests dashboard instead of a list in a dialog. */}
          <button
            type="button"
            className="btn btn-link"
            style={{ padding: 0, fontSize: 13, display: 'block', textAlign: 'left' }}
            onClick={() => { setOpen(false); navigate('/pilot'); }}
          >
            My requests
          </button>
          {panelLinks.length > 0 && (
            <>
              <hr className="shell__user-menu-pop__divider" />
              {panelLinks.map((p) => (
                <button
                  key={p.url}
                  type="button"
                  className="btn btn-link"
                  style={{ padding: 0, fontSize: 13, display: 'block', textAlign: 'left' }}
                  onClick={() => { setOpen(false); openInBrowser(p.url); }}
                >
                  {p.label} ↗
                </button>
              ))}
            </>
          )}
          <hr className="shell__user-menu-pop__divider" />
          {/* SIGN OUT IS GONE (Bill 2026-09-11). Identity here comes from
              Teams SSO; there is nothing app-side to sign out OF. The button
              cleared local state and dropped the user on an "Access
              restricted" page with no way back but restarting the app - an
              option whose only outcome was a support call. This popover is
              now the home of the positive system status: who, where, which
              build, and whether the services the app depends on are up. */}
          <div className="shell__user-menu-pop__about">
            <SystemAboutPanel compact />
          </div>
        </div>
      )}
    </div>
  );
}
